import React, { useState, useEffect, useCallback } from 'react';
import { useFetching } from 'components/hooks/useFetching';
import BrandingSettingsService from 'services/BrandingSettingsService';
import { NotificationManager } from 'react-notifications';
import Strings from 'values/Strings';
import Loader from 'components/UI/loader/Loader';
import { useBranding } from 'components/UI/branding/BrandingProvider';
import LogoUpload from './LogoUpload';

// Импорт стилей
import './BrandingSettingsManager.css';

// Импорт новых компонентов
import TabNavigation from './components/TabNavigation';
import FormSection from './components/FormSection';
import SettingsCard from './components/SettingsCard';
import LivePreview from './components/LivePreview';

// Константа для базового URL
const NODE_HOST = process.env.REACT_APP_NODE_HOST || 'http://localhost:8080';

// Описания ключей для JSON-подсказок
const FIELD_HINTS = {
  primary_color: 'Основной брендовый цвет (кнопки/акценты)',
  secondary_color: 'Вторичный цвет (hover, дополнительные акценты)',
  text_color: 'Обычный текст',
  text_light_color: 'Вторичный/приглушенный текст',
  text_dark_color: 'Заголовки/контрастный текст',
  text_on_primary: 'Текст поверх основного цвета (кнопки)',
  text_active: 'Текст активных элементов',
  text_inactive: 'Текст неактивных элементов',
  text_sidebar: 'Текст в сайдбаре',
  text_footer: 'Текст в футере',
  text_header: 'Текст в шапке',
  text_main: 'Текст в основной области',
  background_color: 'Главный фон сайта',
  background_light: 'Светлый фон блоков',
  background_dark: 'Тёмный фон блоков',
  background_hover: 'Фон при наведении',
  border_color: 'Цвет границ',
  border_light: 'Светлые границы',
  border_dark: 'Тёмные границы',
  border_focus: 'Цвет границы при фокусе',
  active_color: 'Цвет активного состояния',
  hover_color: 'Цвет при наведении (hover)',
  disabled_color: 'Цвет неактивных элементов',
  disabled_background: 'Фон неактивных элементов',
  success_color: 'Цвет успеха (текст)',
  success_background: 'Фон успеха',
  error_color: 'Цвет ошибки (текст)',
  error_background: 'Фон ошибки',
  warning_color: 'Цвет предупреждения (текст)',
  warning_background: 'Фон предупреждения',
  info_color: 'Цвет информации (текст)',
  info_background: 'Фон информации',
  link_color: 'Цвет ссылок',
  link_hover_color: 'Цвет ссылок при наведении',
  shadow_color: 'Цвет тени элементов',
  burger_background: 'Фон бургер-меню',
  burger_color: 'Цвет иконки бургер-меню',
  burger_hover_background: 'Фон бургер-меню при наведении',
  burger_hover_color: 'Цвет иконки бургер-меню при наведении',
  burger_active_background: 'Фон активного бургер-меню',
  burger_active_color: 'Цвет активного бургер-меню',
  input_background: 'Фон полей ввода',
  input_text: 'Текст в поле ввода',
  input_placeholder: 'Подсказка (placeholder) в поле ввода',
  input_border: 'Граница поля ввода',
  input_focus_border: 'Граница поля при фокусе',
  input_focus_background: 'Фон поля при фокусе',
  logo_url: 'Путь к логотипу (начиная с /files/...)',
  is_active: 'Флаг активности набора настроек'
};

// Генерирует JSON с подсказками (в формате JSONC с // комментариями)
const buildCommentedJson = (data) => {
  const ordered = { ...data };
  const lines = ['{'];
  const keys = Object.keys(ordered);
  keys.forEach((key, idx) => {
    const value = ordered[key];
    const hint = FIELD_HINTS[key] ? ` // ${FIELD_HINTS[key]}` : '';
    const jsonValue = typeof value === 'string' ? JSON.stringify(value) : JSON.stringify(value);
    const comma = idx < keys.length - 1 ? ',' : '';
    lines.push(`  "${key}": ${jsonValue},${comma}${hint}`);
  });
  lines.push('}');
  return lines.join('\n');
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    NotificationManager.success('Скопировано в буфер обмена');
  } catch (_) {
    NotificationManager.error('Не удалось скопировать');
  }
};

const BrandingSettingsManager = () => {
  const { updateLogo, updateBrandingSettings } = useBranding();
  const [settingsList, setSettingsList] = useState([]);
  const [editingSettings, setEditingSettings] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [showPreview, setShowPreview] = useState(false);
  
  // Расширенные данные формы с полным набором полей
  const [formData, setFormData] = useState({
    // Основные цвета
    primary_color: '#4783ed',
    secondary_color: '#81a8ec',

    // Цвета текста
    text_color: '#333333',
    text_light_color: '#666666',
    text_dark_color: '#000000',
    text_on_primary: '#ffffff',
    text_active: '#ffffff',
    text_inactive: '#ffffff',

    // Цвета текста для разных областей
    text_sidebar: '#ffffff',
    text_footer: '#666666',
    text_header: '#ffffff',
    text_main: '#333333',

    // Цвета фона
    background_color: '#f8f9fa',
    background_light: '#ffffff',
    background_dark: '#f0f0f0',
    background_hover: '#e9ecef',

    // Цвета обводок/границ
    border_color: '#dee2e6',
    border_light: '#e9ecef',
    border_dark: '#adb5bd',
    border_focus: '#4783ed',

    // Цвета состояний
    active_color: '#ffffff',
    hover_color: '#81a8ec',
    disabled_color: '#6c757d',
    disabled_background: '#e9ecef',

    // Цвета уведомлений
    success_color: '#28a745',
    success_background: '#d4edda',
    error_color: '#dc3545',
    error_background: '#f8d7da',
    warning_color: '#ffc107',
    warning_background: '#fff3cd',
    info_color: '#17a2b8',
    info_background: '#d1ecf1',

    // Цвета специальных элементов
    link_color: '#4783ed',
    link_hover_color: '#81a8ec',
    shadow_color: 'rgba(0,0,0,0.1)',

    // Цвета бургер-меню
    burger_background: '#ffffff',
    burger_color: '#4783ed',
    burger_hover_background: '#f8f9fa',
    burger_hover_color: '#81a8ec',
    burger_active_background: '#ffffff',
    burger_active_color: '#000000',

    // Цвета полей ввода
    input_background: '#ffffff',
    input_text: '#333333',
    input_placeholder: '#999999',
    input_border: '#dee2e6',
    input_focus_border: '#4783ed',
    input_focus_background: '#ffffff',

    logo_url: '',
    is_active: false
  });

  const [jsonCode, setJsonCode] = useState(JSON.stringify(formData, null, 2));
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    if (activeTab === 'json') setJsonCode(JSON.stringify(formData, null, 2));
  }, [formData, activeTab]);

  // Конфигурация табов
  const tabs = [
    {
      id: 'all-colors',
      label: 'Все цвета',
      icon: '🎨',
      description: 'Все настраиваемые цвета с поиском'
    },
    {
      id: 'json',
      label: 'JSON',
      icon: '🛠️',
      description: 'Редактирование через JSON с подсказками'
    },
    {
      id: 'basic',
      label: 'Основные',
      icon: '🎯',
      description: 'Главные брендовые цвета (primary, secondary)'
    },
    {
      id: 'text',
      label: 'Текст',
      icon: '📝',
      description: 'Цвета текста для разных областей'
    },
    {
      id: 'background',
      label: 'Фоны',
      icon: '🖼️',
      description: 'Фоновые цвета страниц и элементов'
    },
    {
      id: 'borders',
      label: 'Границы',
      icon: '🔲',
      description: 'Цвета обводок и границ'
    },
    {
      id: 'states',
      label: 'Состояния',
      icon: '⚡',
      description: 'Цвета для hover, active, disabled'
    },
    {
      id: 'notifications',
      label: 'Уведомления',
      icon: '🔔',
      description: 'Цвета для success, error, warning, info'
    },
    {
      id: 'links',
      label: 'Ссылки и элементы',
      icon: '🔗',
      description: 'Ссылки, бургер-меню, тени'
    },
    {
      id: 'inputs',
      label: 'Поля ввода',
      icon: '📋',
      description: 'Цвета полей форм и инпутов'
    },
    {
      id: 'logo',
      label: 'Логотип',
      icon: '🖼️',
      description: 'Загрузка и предпросмотр логотипа'
    }
  ];

  // Конфигурация полей для каждой секции
  const getFieldsForTab = (tabId) => {
    const fieldsConfig = {
      basic: [
        {
          name: 'primary_color',
          type: 'color',
          label: 'Основной брендовый цвет',
          placeholder: '#4783ed',
          description: 'Главный цвет бренда, используется для кнопок, ссылок и основных акцентов интерфейса',
          cssVariables: ['--main-color', '--main-color-border']
        },
        {
          name: 'secondary_color',
          type: 'color',
          label: 'Вторичный цвет',
          placeholder: '#81a8ec',
          description: 'Дополнительный цвет, используется при наведении (hover) и для второстепенных элементов',
          cssVariables: ['--secondary-color']
        }
      ],
      text: [
        {
          name: 'text_color',
          type: 'color',
          label: 'Основной текст',
          placeholder: '#333333',
          description: 'Цвет обычного текста на странице',
          cssVariables: ['--text-color']
        },
        {
          name: 'text_light_color',
          type: 'color',
          label: 'Светлый текст',
          placeholder: '#666666',
          description: 'Цвет вторичного текста, подписей и примечаний',
          cssVariables: ['--text-light-color']
        },
        {
          name: 'text_dark_color',
          type: 'color',
          label: 'Темный текст',
          placeholder: '#000000',
          description: 'Цвет заголовков и важного текста',
          cssVariables: ['--text-dark-color']
        },
        {
          name: 'text_on_primary',
          type: 'color',
          label: 'Текст на основном цвете',
          placeholder: '#ffffff',
          description: 'Цвет текста на кнопках и цветных фонах',
          cssVariables: ['--text-on-primary']
        },
        {
          name: 'text_active',
          type: 'color',
          label: 'Текст активных элементов',
          placeholder: '#ffffff',
          description: 'Цвет текста выбранных элементов в сайдбаре и заголовках',
          cssVariables: ['--text-active']
        },
        {
          name: 'text_inactive',
          type: 'color',
          label: 'Текст неактивных элементов',
          placeholder: '#ffffff',
          description: 'Цвет текста невыбранных элементов в сайдбаре',
          cssVariables: ['--text-inactive']
        },
        {
          name: 'text_sidebar',
          type: 'color',
          label: 'Текст сайдбара',
          placeholder: '#ffffff',
          description: 'Цвет текста в боковой панели навигации',
          cssVariables: ['--text-sidebar']
        },
        {
          name: 'text_footer',
          type: 'color',
          label: 'Текст футера',
          placeholder: '#666666',
          description: 'Цвет текста в подвале страницы',
          cssVariables: ['--text-footer']
        },
        {
          name: 'text_header',
          type: 'color',
          label: 'Текст шапки',
          placeholder: '#ffffff',
          description: 'Цвет текста в верхней шапке сайта',
          cssVariables: ['--text-header']
        },
        {
          name: 'text_main',
          type: 'color',
          label: 'Текст основной области',
          placeholder: '#333333',
          description: 'Цвет текста в главном контенте страницы',
          cssVariables: ['--text-main']
        }
      ],
      background: [
        {
          name: 'background_color',
          type: 'color',
          label: 'Основной фон',
          placeholder: '#f8f9fa',
          description: 'Фон страниц и контейнеров',
          cssVariables: ['--background-color']
        },
        {
          name: 'background_light',
          type: 'color',
          label: 'Светлый фон',
          placeholder: '#ffffff',
          description: 'Фон карточек и модальных окон',
          cssVariables: ['--background-light']
        },
        {
          name: 'background_dark',
          type: 'color',
          label: 'Темный фон',
          placeholder: '#f0f0f0',
          description: 'Фон для выделенных секций',
          cssVariables: ['--background-dark']
        },
        {
          name: 'background_hover',
          type: 'color',
          label: 'Фон при наведении',
          placeholder: '#e9ecef',
          description: 'Фон элементов при наведении мыши',
          cssVariables: ['--background-hover']
        }
      ],
      borders: [
        {
          name: 'border_color',
          type: 'color',
          label: 'Основная обводка',
          placeholder: '#dee2e6',
          description: 'Стандартные границы элементов',
          cssVariables: ['--border-color']
        },
        {
          name: 'border_light',
          type: 'color',
          label: 'Светлая обводка',
          placeholder: '#e9ecef',
          description: 'Тонкие границы и разделители',
          cssVariables: ['--border-light']
        },
        {
          name: 'border_dark',
          type: 'color',
          label: 'Темная обводка',
          placeholder: '#adb5bd',
          description: 'Выделенные границы',
          cssVariables: ['--border-dark']
        },
        {
          name: 'border_focus',
          type: 'color',
          label: 'Обводка при фокусе',
          placeholder: '#4783ed',
          description: 'Граница активных полей ввода',
          cssVariables: ['--border-focus']
        }
      ],
      states: [
        {
          name: 'active_color',
          type: 'color',
          label: 'Цвет активного элемента',
          placeholder: '#ffffff',
          description: 'Цвет для активных кнопок, ссылок и выбранных элементов интерфейса',
          cssVariables: ['--active-color']
        },
        {
          name: 'hover_color',
          type: 'color',
          label: 'Цвет при наведении (hover)',
          placeholder: '#81a8ec',
          description: 'Цвет элементов при наведении курсора мыши - применяется ко всем интерактивным элементам',
          cssVariables: ['--hover-color']
        },
        {
          name: 'disabled_color',
          type: 'color',
          label: 'Цвет неактивного элемента',
          placeholder: '#6c757d',
          description: 'Цвет текста для отключенных (disabled) кнопок и полей ввода',
          cssVariables: ['--disabled-color']
        },
        {
          name: 'disabled_background',
          type: 'color',
          label: 'Фон неактивного элемента',
          placeholder: '#e9ecef',
          description: 'Фоновый цвет для отключенных (disabled) кнопок и полей ввода',
          cssVariables: ['--disabled-background']
        }
      ],
      notifications: [
        {
          name: 'success_color',
          type: 'color',
          label: 'Успех (цвет)',
          placeholder: '#28a745',
          description: 'Цвет текста успешных сообщений',
          cssVariables: ['--success-color']
        },
        {
          name: 'success_background',
          type: 'color',
          label: 'Успех (фон)',
          placeholder: '#d4edda',
          description: 'Фон успешных уведомлений',
          cssVariables: ['--success-background']
        },
        {
          name: 'error_color',
          type: 'color',
          label: 'Ошибка (цвет)',
          placeholder: '#dc3545',
          description: 'Цвет текста ошибок',
          cssVariables: ['--error-color']
        },
        {
          name: 'error_background',
          type: 'color',
          label: 'Ошибка (фон)',
          placeholder: '#f8d7da',
          description: 'Фон сообщений об ошибках',
          cssVariables: ['--error-background']
        },
        {
          name: 'warning_color',
          type: 'color',
          label: 'Предупреждение (цвет)',
          placeholder: '#ffc107',
          description: 'Цвет текста предупреждений',
          cssVariables: ['--warning-color']
        },
        {
          name: 'warning_background',
          type: 'color',
          label: 'Предупреждение (фон)',
          placeholder: '#fff3cd',
          description: 'Фон предупреждающих сообщений',
          cssVariables: ['--warning-background']
        },
        {
          name: 'info_color',
          type: 'color',
          label: 'Информация (цвет)',
          placeholder: '#17a2b8',
          description: 'Цвет текста информационных сообщений',
          cssVariables: ['--info-color']
        },
        {
          name: 'info_background',
          type: 'color',
          label: 'Информация (фон)',
          placeholder: '#d1ecf1',
          description: 'Фон информационных уведомлений',
          cssVariables: ['--info-background']
        }
      ],
      links: [
        {
          name: 'link_color',
          type: 'color',
          label: 'Цвет ссылок',
          placeholder: '#4783ed',
          description: 'Цвет обычных ссылок на сайте',
          cssVariables: ['--link-color']
        },
        {
          name: 'link_hover_color',
          type: 'color',
          label: 'Цвет ссылок при наведении',
          placeholder: '#81a8ec',
          description: 'Цвет ссылок при наведении курсора мыши',
          cssVariables: ['--link-hover-color']
        },
        {
          name: 'shadow_color',
          type: 'color',
          label: 'Цвет теней',
          placeholder: 'rgba(0,0,0,0.1)',
          description: 'Цвет теней для карточек и элементов интерфейса',
          cssVariables: ['--shadow-color']
        },
        {
          name: 'burger_background',
          type: 'color',
          label: 'Фон бургер-меню',
          placeholder: '#ffffff',
          description: 'Фоновый цвет бургер-меню (кнопка мобильного меню)',
          cssVariables: ['--burger-background']
        },
        {
          name: 'burger_color',
          type: 'color',
          label: 'Цвет иконки бургер-меню',
          placeholder: '#4783ed',
          description: 'Цвет иконки бургер-меню в обычном состоянии',
          cssVariables: ['--burger-color']
        },
        {
          name: 'burger_hover_background',
          type: 'color',
          label: 'Фон бургер-меню при наведении',
          placeholder: '#f8f9fa',
          description: 'Фоновый цвет бургер-меню при наведении курсора',
          cssVariables: ['--burger-hover-background']
        },
        {
          name: 'burger_hover_color',
          type: 'color',
          label: 'Цвет иконки при наведении',
          placeholder: '#81a8ec',
          description: 'Цвет иконки бургер-меню при наведении курсора',
          cssVariables: ['--burger-hover-color']
        },
        {
          name: 'burger_active_background',
          type: 'color',
          label: 'Фон активного бургер-меню',
          placeholder: '#ffffff',
          description: 'Фоновый цвет бургер-меню в активном (открытом) состоянии',
          cssVariables: ['--burger-active-background']
        },
        {
          name: 'burger_active_color',
          type: 'color',
          label: 'Цвет активного бургер-меню',
          placeholder: '#000000',
          description: 'Цвет иконки бургер-меню в активном (открытом) состоянии',
          cssVariables: ['--burger-active-color']
        }
      ],
      inputs: [
        {
          name: 'input_background',
          type: 'color',
          label: 'Фон поля ввода',
          placeholder: '#ffffff',
          description: 'Фоновый цвет всех полей ввода (input, textarea, select) в формах',
          cssVariables: ['--input-background']
        },
        {
          name: 'input_text',
          type: 'color',
          label: 'Цвет текста в поле',
          placeholder: '#333333',
          description: 'Цвет текста, который пользователь вводит в поля форм',
          cssVariables: ['--input-text']
        },
        {
          name: 'input_placeholder',
          type: 'color',
          label: 'Цвет подсказки (placeholder)',
          placeholder: '#999999',
          description: 'Цвет текста подсказки (placeholder) в пустых полях ввода',
          cssVariables: ['--input-placeholder']
        },
        {
          name: 'input_border',
          type: 'color',
          label: 'Цвет границы поля',
          placeholder: '#dee2e6',
          description: 'Цвет границы полей ввода в обычном состоянии',
          cssVariables: ['--input-border']
        },
        {
          name: 'input_focus_border',
          type: 'color',
          label: 'Цвет границы при фокусе',
          placeholder: '#4783ed',
          description: 'Цвет границы поля ввода когда на него установлен фокус (кликнули или Tab)',
          cssVariables: ['--input-focus-border']
        },
        {
          name: 'input_focus_background',
          type: 'color',
          label: 'Фон при фокусе',
          placeholder: '#ffffff',
          description: 'Фоновый цвет поля ввода когда на него установлен фокус',
          cssVariables: ['--input-focus-background']
        }
      ]
    };

    return fieldsConfig[tabId] || [];
  };

  // Получить все цветовые поля из всех табов
  const getAllColorFields = () => {
    const allFields = [];
    const tabIds = ['basic', 'text', 'background', 'borders', 'states', 'notifications', 'links', 'inputs'];
    tabIds.forEach(tabId => {
      const fields = getFieldsForTab(tabId);
      allFields.push(...fields);
    });
    return allFields;
  };

  // Фильтрация полей по поисковому запросу
  const getFilteredColorFields = () => {
    if (!searchQuery.trim()) return getAllColorFields();
    const query = searchQuery.toLowerCase();
    return getAllColorFields().filter(field => 
      field.label.toLowerCase().includes(query) ||
      field.name.toLowerCase().includes(query) ||
      field.description.toLowerCase().includes(query) ||
      (field.placeholder && field.placeholder.toLowerCase().includes(query)) ||
      (field.cssVariables && field.cssVariables.some(v => v.toLowerCase().includes(query)))
    );
  };

  // Уведомления
  const notificationSuccess = useCallback((message) => {
    NotificationManager.success(message, Strings.notificationsSuccessTitle, 3000);
  }, []);

  const notificationError = useCallback((error) => {
    NotificationManager.error(error.message || 'Произошла ошибка', Strings.notificationsErrorTitle, 3000);
  }, []);

  // Загрузка списка настроек
  const fetchSettings = useCallback(async () => {
    try {
      const res = await BrandingSettingsService.getAllSettings();
      setSettingsList(res);
    } catch (error) {
      notificationError(error);
    }
  }, [notificationError]);

  const [, loadingList] = useFetching(fetchSettings, false);

  const resetForm = useCallback(() => {
    setFormData({
      primary_color: '#4783ed',
      secondary_color: '#81a8ec',
      text_color: '#333333',
      text_light_color: '#666666',
      text_dark_color: '#000000',
      text_on_primary: '#ffffff',
      text_active: '#ffffff',
      text_inactive: '#ffffff',
      text_sidebar: '#ffffff',
      text_footer: '#666666',
      text_header: '#ffffff',
      text_main: '#333333',
      background_color: '#f8f9fa',
      background_light: '#ffffff',
      background_dark: '#f0f0f0',
      background_hover: '#e9ecef',
      border_color: '#dee2e6',
      border_light: '#e9ecef',
      border_dark: '#adb5bd',
      border_focus: '#4783ed',
      active_color: '#ffffff',
      hover_color: '#81a8ec',
      disabled_color: '#6c757d',
      disabled_background: '#e9ecef',
      success_color: '#28a745',
      success_background: '#d4edda',
      error_color: '#dc3545',
      error_background: '#f8d7da',
      warning_color: '#ffc107',
      warning_background: '#fff3cd',
      info_color: '#17a2b8',
      info_background: '#d1ecf1',
      link_color: '#4783ed',
      link_hover_color: '#81a8ec',
      shadow_color: 'rgba(0,0,0,0.1)',
      burger_background: '#ffffff',
      burger_color: '#4783ed',
      burger_hover_background: '#f8f9fa',
      burger_hover_color: '#81a8ec',
      burger_active_background: '#ffffff',
      burger_active_color: '#000000',
      input_background: '#ffffff',
      input_text: '#333333',
      input_placeholder: '#999999',
      input_border: '#dee2e6',
      input_focus_border: '#4783ed',
      input_focus_background: '#ffffff',
      logo_url: '',
      is_active: false
    });
  }, []);

  // Создание/обновление настроек
  const saveSettings = useCallback(async (data) => {
    try {
      // Принудительно активируем настройки при сохранении
      const dataWithActive = { ...data, is_active: true };
      
      let result;
      if (editingSettings) {
        result = await BrandingSettingsService.updateSettings(editingSettings.id, dataWithActive);
        notificationSuccess('Настройки успешно обновлены и применены');
      } else {
        result = await BrandingSettingsService.createSettings(dataWithActive);
        notificationSuccess('Настройки успешно созданы и применены');
      }

      if (result) {
        // Формируем полный URL для логотипа если нужно
        if (result.logo_url && result.logo_url.startsWith('/files/')) {
          result.logo_url = `${NODE_HOST}${result.logo_url}`;
        }
        // Сразу применяем настройки
        updateBrandingSettings(result);
      }

      // Обновляем список настроек
      await fetchSettings();

      setEditingSettings(null);
      setIsCreating(false);
      resetForm();
    } catch (error) {
      notificationError(error);
    }
  }, [editingSettings, notificationSuccess, updateBrandingSettings, notificationError, resetForm, fetchSettings]);

  const [savingSettings, loadingSave] = useFetching(saveSettings, false);

  // Удаление настроек
  const deleteSettings = useCallback(async (id) => {
    try {
      await BrandingSettingsService.deleteSettings(id);
      notificationSuccess('Настройки успешно удалены');
      fetchSettings();
    } catch (error) {
      notificationError(error);
    }
  }, [notificationSuccess, notificationError, fetchSettings]);

  const [deletingSettings, loadingDelete] = useFetching(deleteSettings, false);

  // Активация настроек
  const activateSettings = useCallback(async (id) => {
    try {
      const result = await BrandingSettingsService.activateSettings(id);
      notificationSuccess('Настройки активированы');

      if (result) {
        if (result.logo_url && result.logo_url.startsWith('/files/')) {
          result.logo_url = `${NODE_HOST}${result.logo_url}`;
        }
        updateBrandingSettings(result);
      }

      fetchSettings();
    } catch (error) {
      notificationError(error);
    }
  }, [notificationSuccess, updateBrandingSettings, notificationError, fetchSettings]);

  const [activatingSettings, loadingActivate] = useFetching(activateSettings, false);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Восстанавливаем все настройки брендинга при загрузке списка настроек
  useEffect(() => {
    if (settingsList.length > 0) {
      const activeSettings = settingsList.find(s => s.is_active);
      if (activeSettings) {
        if (activeSettings.logo_url && activeSettings.logo_url.startsWith('/files/')) {
          activeSettings.logo_url = `${NODE_HOST}${activeSettings.logo_url}`;
        }
        updateBrandingSettings(activeSettings);
      } else {
        updateBrandingSettings({
          primary_color: '#4783ed',
          secondary_color: '#81a8ec',
          text_color: '#333333',
          active_color: '#ffffff',
          background_color: '#f8f9fa',
          logo_url: null
        });
      }
    }
  }, [settingsList, updateBrandingSettings]);

  const handleEdit = useCallback((settings) => {
    setEditingSettings(settings);

    let logoUrl = settings.logo_url || '';
    if (logoUrl && logoUrl.startsWith('/files/')) {
      logoUrl = `${NODE_HOST}${logoUrl}`;
    }

    setFormData({
      primary_color: settings.primary_color,
      secondary_color: settings.secondary_color,
      text_color: settings.text_color || '#333333',
      text_light_color: settings.text_light_color || '#666666',
      text_dark_color: settings.text_dark_color || '#000000',
      text_on_primary: settings.text_on_primary || '#ffffff',
      text_active: settings.text_active || '#ffffff',
      text_inactive: settings.text_inactive || '#ffffff',
      text_sidebar: settings.text_sidebar || '#ffffff',
      text_footer: settings.text_footer || '#666666',
      text_header: settings.text_header || '#ffffff',
      text_main: settings.text_main || '#333333',
      background_color: settings.background_color || '#f8f9fa',
      background_light: settings.background_light || '#ffffff',
      background_dark: settings.background_dark || '#f0f0f0',
      background_hover: settings.background_hover || '#e9ecef',
      border_color: settings.border_color || '#dee2e6',
      border_light: settings.border_light || '#e9ecef',
      border_dark: settings.border_dark || '#adb5bd',
      border_focus: settings.border_focus || '#4783ed',
      active_color: settings.active_color || '#ffffff',
      hover_color: settings.hover_color || '#81a8ec',
      disabled_color: settings.disabled_color || '#6c757d',
      disabled_background: settings.disabled_background || '#e9ecef',
      success_color: settings.success_color || '#28a745',
      success_background: settings.success_background || '#d4edda',
      error_color: settings.error_color || '#dc3545',
      error_background: settings.error_background || '#f8d7da',
      warning_color: settings.warning_color || '#ffc107',
      warning_background: settings.warning_background || '#fff3cd',
      info_color: settings.info_color || '#17a2b8',
      info_background: settings.info_background || '#d1ecf1',
      link_color: settings.link_color || '#4783ed',
      link_hover_color: settings.link_hover_color || '#81a8ec',
      shadow_color: settings.shadow_color || 'rgba(0,0,0,0.1)',
      burger_background: settings.burger_background || '#ffffff',
      burger_color: settings.burger_color || '#4783ed',
      burger_hover_background: settings.burger_hover_background || '#f8f9fa',
      burger_hover_color: settings.burger_hover_color || '#81a8ec',
      burger_active_background: settings.burger_active_background || '#ffffff',
      burger_active_color: settings.burger_active_color || '#000000',
      input_background: settings.input_background || '#ffffff',
      input_text: settings.input_text || '#333333',
      input_placeholder: settings.input_placeholder || '#999999',
      input_border: settings.input_border || '#dee2e6',
      input_focus_border: settings.input_focus_border || '#4783ed',
      input_focus_background: settings.input_focus_background || '#ffffff',
      logo_url: logoUrl,
      is_active: settings.is_active
    });
    setIsCreating(false);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingSettings(null);
    resetForm();
    setIsCreating(true);
  }, [resetForm]);

  const handleCancel = useCallback(() => {
    setEditingSettings(null);
    setIsCreating(false);
    resetForm();

    const activeSettings = settingsList.find(s => s.is_active);
    if (activeSettings) {
      if (activeSettings.logo_url && activeSettings.logo_url.startsWith('/files/')) {
        activeSettings.logo_url = `${NODE_HOST}${activeSettings.logo_url}`;
      }
      updateBrandingSettings(activeSettings);
    } else {
      updateBrandingSettings({
        primary_color: '#4783ed',
        secondary_color: '#81a8ec',
        text_color: '#333333',
        active_color: '#ffffff',
        background_color: '#f8f9fa',
        logo_url: null
      });
    }
  }, [settingsList, resetForm, updateBrandingSettings]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!formData.primary_color || !formData.secondary_color) {
      NotificationManager.warning('Цвета обязательны', Strings.notificationsWarningTitle, 3000);
      return;
    }

    let logoPath = formData.logo_url || null;
    if (logoPath && logoPath.includes('/files/')) {
      logoPath = logoPath.replace(/^https?:\/\/[^/]+/, '');
    }

    const dataToSave = {
      primary_color: formData.primary_color,
      secondary_color: formData.secondary_color,
      text_color: formData.text_color,
      text_light_color: formData.text_light_color,
      text_dark_color: formData.text_dark_color,
      text_on_primary: formData.text_on_primary,
      text_active: formData.text_active,
      text_inactive: formData.text_inactive,
      text_sidebar: formData.text_sidebar,
      text_footer: formData.text_footer,
      text_header: formData.text_header,
      text_main: formData.text_main,
      background_color: formData.background_color,
      background_light: formData.background_light,
      background_dark: formData.background_dark,
      background_hover: formData.background_hover,
      border_color: formData.border_color,
      border_light: formData.border_light,
      border_dark: formData.border_dark,
      border_focus: formData.border_focus,
      active_color: formData.active_color,
      hover_color: formData.hover_color,
      disabled_color: formData.disabled_color,
      disabled_background: formData.disabled_background,
      success_color: formData.success_color,
      success_background: formData.success_background,
      error_color: formData.error_color,
      error_background: formData.error_background,
      warning_color: formData.warning_color,
      warning_background: formData.warning_background,
      info_color: formData.info_color,
      info_background: formData.info_background,
      link_color: formData.link_color,
      link_hover_color: formData.link_hover_color,
      shadow_color: formData.shadow_color,
      burger_background: formData.burger_background,
      burger_color: formData.burger_color,
      burger_hover_background: formData.burger_hover_background,
      burger_hover_color: formData.burger_hover_color,
      burger_active_background: formData.burger_active_background,
      burger_active_color: formData.burger_active_color,
      input_background: formData.input_background,
      input_text: formData.input_text,
      input_placeholder: formData.input_placeholder,
      input_border: formData.input_border,
      input_focus_border: formData.input_focus_border,
      input_focus_background: formData.input_focus_background,
      logo_url: logoPath,
      is_active: formData.is_active
    };

    console.log('Отправляемые данные:', dataToSave);

    savingSettings(dataToSave);
  }, [formData, savingSettings]);

  const handleDelete = useCallback((id) => {
    if (window.confirm('Вы уверены, что хотите удалить эти настройки?')) {
      deletingSettings(id);
    }
  }, [deletingSettings]);

  const handleActivate = useCallback((id) => {
    if (window.confirm('Активировать эти настройки? Все остальные будут деактивированы.')) {
      activatingSettings(id);
    }
  }, [activatingSettings]);

  const totalLoading = loadingList || loadingSave || loadingDelete || loadingActivate;

  return (
    <Loader localStatus={totalLoading}>
      <div className="branding-settings-manager">
        <div className="branding-settings-header">
          <div className="branding-settings-title">
            <h1 className="branding-settings-title-text">
              <span className="branding-settings-title-icon">🎨</span>
              Управление брендингом
            </h1>
            <p className="branding-settings-title-description">
              Настройте цвета, логотип и стиль вашего сайта
            </p>
          </div>
          
          <div className="branding-settings-actions">
            <button
              className="branding-settings-action-btn preview"
              onClick={() => setShowPreview(!showPreview)}
              disabled={totalLoading}
            >
              <span className="branding-settings-action-icon">👁️</span>
              {showPreview ? 'Скрыть превью' : 'Показать превью'}
            </button>
            
            <button
              className="branding-settings-action-btn create"
              onClick={handleCreate}
              disabled={totalLoading}
            >
              <span className="branding-settings-action-icon">➕</span>
              Создать настройки
            </button>
          </div>
        </div>

        {/* Живой предпросмотр */}
        {showPreview && (
          <div className="branding-settings-preview">
            <LivePreview formData={formData} />
          </div>
        )}

        {/* Модальное окно для создания/редактирования */}
        {(isCreating || editingSettings) && (
          <div className="branding-settings-modal-overlay">
            <div className="branding-settings-modal-container">
              <div className="branding-settings-modal-header">
                <h2 className="branding-settings-modal-title">
                  {editingSettings ? 'Редактирование настроек' : 'Создание новых настроек'}
                </h2>
                <button
                  type="button"
                  className="branding-settings-modal-close"
                  onClick={handleCancel}
                >
                  <span className="branding-settings-modal-close-icon">×</span>
                </button>
              </div>

              <div className="branding-settings-modal-body">
                <form onSubmit={handleSubmit} className="branding-settings-form">
                  <TabNavigation
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={tabs}
                    disabled={totalLoading}
                  />

                  {activeTab === 'all-colors' && (
                    <div className="branding-settings-all-colors-tab" style={{ padding: '24px' }}>
                      <div style={{ marginBottom: 20 }}>
                        <input
                          type="text"
                          placeholder="🔍 Поиск по названию, описанию или значению цвета..."
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            fontSize: 14,
                            borderRadius: 8,
                            border: '1px solid #dee2e6',
                            outline: 'none',
                            transition: 'border-color 0.3s'
                          }}
                          onFocus={e => e.target.style.borderColor = '#4783ed'}
                          onBlur={e => e.target.style.borderColor = '#dee2e6'}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                          Найдено цветов: {getFilteredColorFields().length} из {getAllColorFields().length}
                        </div>
                      </div>
                      <FormSection
                        title="Все настраиваемые цвета"
                        description={`${searchQuery ? 'Результаты поиска' : 'Все доступные цветовые настройки'}`}
                        icon="🎨"
                        fields={getFilteredColorFields()}
                        formData={formData}
                        onFieldChange={handleFieldChange}
                        disabled={totalLoading}
                      />
                    </div>
                  )}

                  {activeTab === 'json' && (
                    <div className="branding-settings-json-tab" style={{ margin: '32px 0' }}>
                      <textarea
                        value={jsonCode}
                        onChange={e => setJsonCode(e.target.value)}
                        className="branding-settings-json-textarea"
                        rows={Math.max(20, Math.min(40, jsonCode.split('\n').length + 2))}
                        style={{ width: '100%', minHeight: 300, fontFamily: 'monospace', fontSize: 14, borderRadius: 8, padding: 12, border: '1px solid #ccc' }}
                      />
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(jsonCode)}
                          style={{ padding: '10px 16px', background: '#4783ed', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                          title="Скопировать чистый JSON без комментариев"
                        >Копировать JSON</button>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(buildCommentedJson(JSON.parse(jsonCode)))}
                          style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                          title="Скопировать JSON с подсказками (JSONC)"
                        >Копировать JSON с подсказками</button>
                        <button
                          type="button"
                          onClick={() => {
                            try {
                              const obj = JSON.parse(jsonCode);
                              setFormData(obj);
                              NotificationManager.success('JSON применён');
                            } catch (err) {
                              NotificationManager.error('Ошибка парсинга JSON!');
                            }
                          }}
                          style={{ padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                        >Применить из JSON</button>
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Предпросмотр JSON с подсказками (только чтение)</label>
                        <textarea
                          readOnly
                          value={(() => { try { return buildCommentedJson(JSON.parse(jsonCode)); } catch { return '// Некорректный JSON'; } })()}
                          style={{ width: '100%', minHeight: 220, fontFamily: 'monospace', fontSize: 13, borderRadius: 8, padding: 12, border: '1px solid #e0e0e0', background: '#fafafa' }}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab !== 'all-colors' && activeTab !== 'json' && (
                    <div className="branding-settings-form-content">
                    <FormSection
                      title={tabs.find(tab => tab.id === activeTab)?.label}
                      description={tabs.find(tab => tab.id === activeTab)?.description}
                      icon={tabs.find(tab => tab.id === activeTab)?.icon}
                      fields={getFieldsForTab(activeTab)}
                      formData={formData}
                      onFieldChange={handleFieldChange}
                      disabled={totalLoading}
                    />

                    {/* Логотип */}
                    {activeTab === 'logo' && (
                      <div className="branding-settings-logo-section">
                        <FormSection
                          title="Логотип"
                          description="Загрузите логотип для вашего сайта"
                          icon="🖼️"
                          fields={[
                            {
                              name: 'logo_upload',
                              type: 'custom',
                              component: ({ onChange, disabled }) => (
                                <LogoUpload
                                  onLogoUploaded={(logoUrl) => {
                                    const logoPath = logoUrl ? logoUrl.replace(/^https?:\/\/[^/]+/, '') : null;
                                    setFormData(prev => ({ ...prev, logo_url: logoPath }));
                                    updateLogo(logoUrl);
                                  }}
                                  currentLogo={formData.logo_url}
                                  eventId="default"
                                  disabled={disabled}
                                />
                              )
                            }
                          ]}
                          formData={formData}
                          onFieldChange={handleFieldChange}
                          disabled={totalLoading}
                        />
                      </div>
                    )}
                    </div>
                  )}

                  <div className="branding-settings-form-actions">
                    <button 
                      type="submit" 
                      className="branding-settings-form-submit"
                      disabled={loadingSave}
                    >
                      <span className="branding-settings-form-submit-icon">
                        {loadingSave ? '⏳' : '💾'}
                      </span>
                      {loadingSave ? 'Сохранение...' : 'Сохранить настройки'}
                    </button>
                    
                    <button 
                      type="button" 
                      className="branding-settings-form-cancel"
                      onClick={handleCancel}
                    >
                      <span className="branding-settings-form-cancel-icon">❌</span>
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Список существующих настроек */}
        <div className="branding-settings-list">
          <div className="branding-settings-list-header">
            <h2 className="branding-settings-list-title">
              <span className="branding-settings-list-title-icon">📋</span>
              Существующие настройки
            </h2>
            <div className="branding-settings-list-count">
              {settingsList.length} {settingsList.length === 1 ? 'настройка' : 'настроек'}
            </div>
          </div>

          {settingsList.length === 0 ? (
            <div className="branding-settings-empty">
              <div className="branding-settings-empty-icon">🎨</div>
              <h3 className="branding-settings-empty-title">Настройки не найдены</h3>
              <p className="branding-settings-empty-description">
                Создайте первую настройку брендинга для вашего сайта
              </p>
              <button
                className="branding-settings-empty-action"
                onClick={handleCreate}
                disabled={totalLoading}
              >
                <span className="branding-settings-empty-action-icon">➕</span>
                Создать первую настройку
              </button>
            </div>
          ) : (
            <div className="branding-settings-grid">
              {settingsList.map((settings) => (
                <SettingsCard
                  key={settings.id}
                  settings={settings}
                  isActive={settings.is_active}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onActivate={handleActivate}
                  isLoading={totalLoading}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Loader>
  );
};

export default BrandingSettingsManager;